(() => {
  const MOBILE_BREAKPOINT = 749;
  const mqMobile = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

  const SELECTORS = {
    card: '.card-wrapper',
    cardInner: '.card__inner',
    price: '.card-information > .price',
    priceTemplate: 'template[data-card-swatch-price]',
    swatchesWrapper: '.card__swatches-wrapper',
    swatchesWraps: '.card__swatches, .colors--grid',
    swatch: '.card-swatch',
    moreSwatch: '.card-swatch.card-swatch--more',
    productLink: 'a.product-link, a.full-unstyled-link',
  };

  const CLASSES = {
    active: 'is-active',
    moved: 'is-moved',
    hiddenByMore: 'is-hidden-by-more',
    more: 'card-swatch--more',
  };

  const state = {
    resizeTimer: null,
    rafScheduled: false,
  };

  const isMobile = () => mqMobile.matches;

  // Helpers
  function scheduleLayoutWork(fn) {
    if (state.rafScheduled) return;
    state.rafScheduled = true;
    requestAnimationFrame(() => {
      state.rafScheduled = false;
      fn();
    });
  }

  function getCardFromNode(node) {
    return node?.closest?.(SELECTORS.card) || null;
  }

  function getCardInner(card) {
    return card?.querySelector?.(SELECTORS.cardInner) || null;
  }

  function getCardPrice(card) {
    return card?.querySelector?.(SELECTORS.price) || null;
  }

  function getProductId(cardInner) {
    return cardInner?.getAttribute?.('data-card-product-id') || '';
  }

  function findPrimaryImg(cardInner, productId) {
    return cardInner?.querySelector?.(`img[data-card-image="${productId}"]`) || null;
  }

  function findSecondaryImg(cardInner, productId) {
    return cardInner?.querySelector?.(`img[data-card-image-secondary="${productId}"]`) || null;
  }

  function setImg(img, src) {
    if (!img || !src) return;
    img.src = src;
    img.removeAttribute('srcset');
    img.setAttribute('srcset', `${src} 533w`);
  }

  function preload(url) {
    if (!url) return;
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
  }

  function loadSecondaryImage(card) {
    const img = card?.querySelector('img[data-card-image-secondary]');
    if (!img || img.dataset.loaded === 'true') return;

    if (img.dataset.src) {
      img.src = img.dataset.src;
    }

    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
    }

    img.dataset.loaded = 'true';
  }

  function observeSecondaryImages(root = document) {
  const cards = root.querySelectorAll(SELECTORS.card);

  if (!('IntersectionObserver' in window)) {
    cards.forEach(loadSecondaryImage);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        loadSecondaryImage(entry.target);
        observer.unobserve(entry.target);
      });
    },
    {
      rootMargin: '100px 0px',
      threshold: 0.01,
    }
  );

  cards.forEach((card) => observer.observe(card));
}

  function findActiveScope(swatch) {
    return (
      swatch.closest('.card__swatches') ||
      swatch.closest('.colors') ||
      swatch.closest('.linked-products') ||
      swatch.closest('.card__swatches-wrapper') ||
      swatch.closest(SELECTORS.card)
    );
  }

  function setActiveSwatch(swatch) {
    const scope = findActiveScope(swatch);
    if (scope) {
      scope
        .querySelectorAll(`${SELECTORS.swatch}.${CLASSES.active}`)
        .forEach((el) => el.classList.remove(CLASSES.active));
    }

    swatch.classList.add(CLASSES.active);

    const input = swatch.querySelector('input[type="radio"], input[type="checkbox"]');
    if (input) input.checked = true;
  }

  function swapCardImagesFromSwatch(swatch) {
    const card = getCardFromNode(swatch);
    const cardInner = getCardInner(card);
    if (!cardInner) return;

    const productId = getProductId(cardInner);
    if (!productId) return;

    const primary = findPrimaryImg(cardInner, productId);
    const secondary = findSecondaryImg(cardInner, productId);

    const newPrimarySrc = swatch.dataset.image533 || swatch.dataset.image;
    const newSecondarySrc =
      swatch.dataset.hoverImage533 ||
      swatch.dataset.hoverImage ||
      swatch.dataset.image533 ||
      swatch.dataset.image;

    setImg(primary, newPrimarySrc);
    setImg(secondary, newSecondarySrc);

    const variantUrl = swatch.dataset.variantUrl;
    if (variantUrl) {
      const link = cardInner.querySelector('a.product-link');
      if (link) link.href = variantUrl;
    }
  }

  function swapCardPriceFromSwatch(swatch) {
    const card = getCardFromNode(swatch);
    const price = getCardPrice(card);
    const priceTemplate = swatch.querySelector(SELECTORS.priceTemplate);
    if (!price || !priceTemplate) return;

    price.replaceWith(priceTemplate.content.cloneNode(true));
  }

  // Swatch overflow
  function ensureMoreSwatch(wrap) {
    let more = wrap.querySelector(SELECTORS.moreSwatch);
    if (!more) {
      more = document.createElement('div');
      more.className = `${SELECTORS.swatch.replace('.', '')} ${CLASSES.more}`;
      more.innerHTML = '<span class="card-swatch__more-text"></span>';
      wrap.appendChild(more);
    }
    more.style.display = 'none';
    return more;
  }

  function updateSwatchOverflow(root = document) {
    const mobile = isMobile();
    const wraps = root.querySelectorAll(SELECTORS.swatchesWraps);

    wraps.forEach((wrap) => {
      const swatches = Array.from(
        wrap.querySelectorAll(`${SELECTORS.swatch}:not(.${CLASSES.more})`)
      );

      // reset
      swatches.forEach((s) => s.classList.remove(CLASSES.hiddenByMore));

      const more = ensureMoreSwatch(wrap);
      if (swatches.length === 0) return;

      const isGrouped = wrap.classList.contains('colors--grid');
      const mobileMaxVisible = isGrouped ? 4 : 4;

      if (mobile) {
        const hiddenCount = Math.max(0, swatches.length - mobileMaxVisible);

        swatches.forEach((s, idx) => {
          if (idx >= mobileMaxVisible) s.classList.add(CLASSES.hiddenByMore);
        });

        if (hiddenCount > 0) {
          more.querySelector('.card-swatch__more-text').textContent = `+${hiddenCount}`;
          more.style.display = 'flex';
        }
        return;
      }

      // Desktop: show only first row; reserve one slot for "+more"
      wrap.offsetHeight;

      const firstRowTop = swatches[0].offsetTop;
      let visibleCount = swatches.filter((s) => s.offsetTop === firstRowTop).length;
      let hiddenCount = swatches.length - visibleCount;

      if (hiddenCount <= 0) return;

      visibleCount = Math.max(0, visibleCount - 1);
      hiddenCount = swatches.length - visibleCount;

      swatches.forEach((s, idx) => {
        if (idx >= visibleCount) s.classList.add(CLASSES.hiddenByMore);
      });

      more.querySelector('.card-swatch__more-text').textContent = `+${hiddenCount}`;
      more.style.display = 'flex';
    });
  }

  // Move swatches on mobile
  function moveSwatches() {
    const mobile = isMobile();

    document.querySelectorAll(SELECTORS.card).forEach((card) => {
      const swatchesWrapper = card.querySelector(SELECTORS.swatchesWrapper);
      if (!swatchesWrapper) return;

      const inner = card.querySelector(SELECTORS.cardInner);
      const price = card.querySelector(SELECTORS.price);
      if (!inner || !price) return;

      if (mobile) {
        if (!swatchesWrapper.classList.contains(CLASSES.moved)) {
          swatchesWrapper.classList.add(CLASSES.moved);

          swatchesWrapper.style.position = 'static';
          swatchesWrapper.style.opacity = '1';
          swatchesWrapper.style.visibility = 'visible';
          swatchesWrapper.style.transform = 'none';
          swatchesWrapper.style.background = 'transparent';
          swatchesWrapper.style.height = 'auto';
          swatchesWrapper.style.padding = '0';
          swatchesWrapper.style.marginTop = '6px';

          price.insertAdjacentElement('afterend', swatchesWrapper);
        }
      } else {
        if (swatchesWrapper.classList.contains(CLASSES.moved)) {
          swatchesWrapper.classList.remove(CLASSES.moved);
          swatchesWrapper.removeAttribute('style');
          inner.appendChild(swatchesWrapper);
        }
      }
    });
  }

  function moveSwatchToFront(swatch) {
    const wrap = swatch?.parentElement;
    if (!wrap) return;

    if (wrap.firstElementChild !== swatch) {
      wrap.insertBefore(swatch, wrap.firstElementChild);
    }
  }

  //  Preselect active swatch 
  function preselectCard(card) {
    const inner = card.querySelector(SELECTORS.cardInner);
    if (!inner) return;

    const initialVariantId = inner.dataset.cardInitialVariantId;
    const thumbMediaId = inner.dataset.cardThumbMediaId;

    const swatches = Array.from(
      card.querySelectorAll(`${SELECTORS.swatch}:not(.${CLASSES.more})`)
    );
    if (!swatches.length) return;

    let target = null;

    if (thumbMediaId) {
      target = swatches.find((s) => s.dataset.mediaId === thumbMediaId) || null;
    }
    if (!target && initialVariantId) {
      target = swatches.find((s) => s.dataset.variantId === initialVariantId) || null;
    }
    if (!target) {
      target = swatches.find((s) => !s.classList.contains(CLASSES.hiddenByMore)) || swatches[0];
    }

    if (target) {
      moveSwatchToFront(target);
      setActiveSwatch(target);
    }
  }

  function preselectAll(root = document) {
    root.querySelectorAll(SELECTORS.card).forEach(preselectCard);
  }

  // Click handling
  function handleDocumentClick(e) {
    const el = e.target instanceof Element ? e.target : e.target?.parentElement;
    const swatch = el?.closest?.(SELECTORS.swatch);
    if (!swatch) return;

    if (swatch.classList.contains(CLASSES.more)) {
      e.preventDefault();
      e.stopPropagation();

      const card = getCardFromNode(swatch);
      const link = card?.querySelector?.(SELECTORS.productLink);
      if (link?.href) window.location.href = link.href;
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

    setActiveSwatch(swatch);
    swapCardImagesFromSwatch(swatch);
    swapCardPriceFromSwatch(swatch);
  }

  function runAll(root = document) {
    scheduleLayoutWork(() => {
      moveSwatches();
      updateSwatchOverflow(root);
      preselectAll(root);
    });
  }

  function onResize() {
    clearTimeout(state.resizeTimer);
    state.resizeTimer = setTimeout(() => runAll(document), 140);
  }

  function init() {
    runAll(document);

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => observeSecondaryImages(document));
    } else {
      setTimeout(() => observeSecondaryImages(document), 500);
    }

    // Re-init when Shopify injects recommendations HTML (AJAX)
    document.querySelectorAll('product-recommendations').forEach((pr) => {
      const obs = new MutationObserver(() => {
        if (pr.querySelector(SELECTORS.card)) runAll(pr);
      });

      obs.observe(pr, { childList: true, subtree: true });
    });

    document.addEventListener('click', handleDocumentClick, true);

    document.addEventListener('pointerenter', (e) => {
      const el = e.target instanceof Element ? e.target : e.target?.parentElement;
      const swatch = el?.closest?.(SELECTORS.swatch);
      if (!swatch || swatch.classList.contains(CLASSES.more)) return;

      preload(swatch.dataset.image533 || swatch.dataset.image);
      preload(swatch.dataset.hoverImage533 || swatch.dataset.hoverImage);
    }, true);

    document.addEventListener('pointerenter', (e) => {
      const el = e.target instanceof Element ? e.target : e.target?.parentElement;
      const card = el?.closest?.(SELECTORS.card);

      if (!card) return;

      loadSecondaryImage(card);
    }, true);

    window.addEventListener('resize', onResize);

    if (mqMobile.addEventListener) {
      mqMobile.addEventListener('change', () => runAll(document));
    } else {
      mqMobile.addListener(() => runAll(document));
    }

    document.addEventListener('shopify:section:load', (e) => {
      runAll(e.target || document);
    });

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// PLP Sold Out badge sync: hides/shows the Sold Out badge based on the active color swatch's availability
(() => {
  const soldOut = (s) => !s || s.classList.contains('disabled') || s.dataset.variantAvailable === 'false';

  function sync(card) {
    const swatches = [...card.querySelectorAll('.card-swatch:not(.card-swatch--more)')];
    if (!swatches.length) return;

    const hasAvailabilityData = swatches.some(
      (s) => s.classList.contains('disabled') || s.dataset.variantAvailable !== undefined
    );

    // If there is no variant availability data, keep the initial Liquid state.
    if (!hasAvailabilityData) return;

    const active = card.querySelector('.card-swatch.is-active') || swatches[0];
    const isSoldOut = soldOut(active);

    const badges = card.querySelectorAll('.card-sold-out-badge');

    badges.forEach((badge) => {
      if (isSoldOut) {
        badge.style.removeProperty('display');
      } else {
        badge.style.setProperty('display', 'none', 'important');
      }
    });
  }

  const syncAll = () => document.querySelectorAll('.card-wrapper').forEach(sync);

  document.addEventListener('click', (e) => {
    const sw = (e.target instanceof Element ? e.target : e.target?.parentElement)?.closest?.('.card-swatch:not(.card-swatch--more)');
    if (sw) setTimeout(() => sync(sw.closest('.card-wrapper')), 0);
  }, true);

  const mo = new MutationObserver(() => syncAll());
  mo.observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });

  let n = 0;
  const iv = setInterval(() => { syncAll(); if (++n >= 20) clearInterval(iv); }, 250);

  window.addEventListener('load', syncAll);
  document.addEventListener('shopify:section:load', syncAll);
})();